import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Check, X, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import logo from "@/assets/logo_final.png";

const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const passwordRules = [
{ key: "min", label: "Mínimo de 5 caracteres", test: (p: string) => p.length >= 5 },
{ key: "upper", label: "Uma letra maiúscula", test: (p: string) => /[A-Z]/.test(p) },
{ key: "lower", label: "Uma letra minúscula", test: (p: string) => /[a-z]/.test(p) },
{ key: "number", label: "Um número", test: (p: string) => /\d/.test(p) },
{ key: "special", label: "Um caractere especial (!@#$%...)", test: (p: string) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p) }];


const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get("next");
  const nextQuery = nextParam ? `?next=${encodeURIComponent(nextParam)}` : "";

  const ruleResults = useMemo(() => passwordRules.map((r) => ({ ...r, passed: r.test(password) })), [password]);
  const allRulesPassed = ruleResults.every((r) => r.passed);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name || !email || !phone || !password || !confirmPassword) {
      setError("Preencha todos os campos");
      return;
    }
    if (!allRulesPassed) {
      setError("A senha não atende todos os requisitos");
      return;
    }
    if (!passwordsMatch) {
      setError("As senhas não coincidem");
      return;
    }

    setSubmitting(true);
    const phoneDigits = phone.replace(/\D/g, "");
    const result = await register(name, email, phoneDigits, password);
    setSubmitting(false);

    if (result.error) {
      setError(result.error);
    } else {
      navigate(`/register/confirmation${nextQuery}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-8 md:p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-6 md:mb-8">
          <img src={logo} alt="Nutrir Corpo & Mente" className="w-36 h-36 md:w-32 md:h-32 mx-auto mb-2 object-contain" />
        </div>

        <div className="bg-card rounded-2xl p-6 shadow-card border border-border">
          <h2 className="text-2xl md:text-xl font-semibold text-foreground mb-6 text-center">Cadastro</h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-base md:text-sm">Nome</Label>
              <Input id="name" placeholder="Seu nome completo" value={name} onChange={(e) => setName(e.target.value)} className="h-[52px] text-base md:h-10 md:text-sm" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-base md:text-sm">Email</Label>
              <Input id="email" type="email" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-[52px] text-base md:h-10 md:text-sm" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-base md:text-sm">Telefone</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="(11) 99999-9999"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                className="h-[52px] text-base md:h-10 md:text-sm" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-base md:text-sm">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPass ? "text" : "password"}
                  placeholder="Crie uma senha segura"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-[52px] text-base md:h-10 md:text-sm" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPass(!showPass)}>
                  {showPass ? <EyeOff className="w-5 h-5 md:w-4 md:h-4" /> : <Eye className="w-5 h-5 md:w-4 md:h-4" />}
                </button>
              </div>
            </div>

            {/* Password strength checklist */}
            {password.length > 0 &&
            <div className="rounded-xl border border-border bg-muted/50 p-3 space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Requisitos de senha segura:</p>
                {ruleResults.map((r) =>
              <div key={r.key} className="flex items-center gap-2 text-xs">
                    {r.passed ?
                <Check className="w-3.5 h-3.5 text-primary shrink-0" /> :
                <X className="w-3.5 h-3.5 text-destructive shrink-0" />
                }
                    <span className={r.passed ? "text-foreground" : "text-muted-foreground"}>{r.label}</span>
                  </div>
              )}
              </div>
            }

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-base md:text-sm">Confirmar Senha</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  placeholder="Repita a senha"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-[52px] text-base md:h-10 md:text-sm" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowConfirm(!showConfirm)}>
                  {showConfirm ? <EyeOff className="w-5 h-5 md:w-4 md:h-4" /> : <Eye className="w-5 h-5 md:w-4 md:h-4" />}
                </button>
              </div>
              {confirmPassword.length > 0 && !passwordsMatch &&
              <p className="text-xs text-destructive">As senhas não coincidem</p>
              }
            </div>

            <p className={`text-sm text-destructive ${error ? "block" : "hidden"}`}>{error}</p>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-[52px] md:h-10 text-base md:text-sm font-semibold gradient-primary text-primary-foreground hover:opacity-90 transition-opacity">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
              Criar conta
            </Button>
          </form>
          <div className="mt-5 text-center text-base md:text-sm text-muted-foreground">
            Já tem conta?{" "}
            <Link to={`/login${nextQuery}`} className="text-primary font-medium hover:underline">Entrar</Link>
          </div>
        </div>
      </motion.div>
    </div>);

};

export default Register;
